import { useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GalleryPhoto {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
}

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_featured_image?: string;
}

// ─── Slot Group Definitions ───────────────────────────────────────────────────

const locationImageDef: SlotGroupDefinition = {
  label: 'Location Banner Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'City view',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a property photo that conveys a sense of place — something that highlights the neighborhood or wider surroundings rather than a tight interior. Good options include an aerial or drone flyover, an exterior or rooftop view that captures the setting, or a shot featuring a recognizable nearby landmark or attraction. Favor whichever best showcases this property’s actual location.',
    context:
      'Full-bleed background image behind the home page neighborhood/location call-to-action ("Paradise is Calling in [city]"); text overlays the left side, so prefer a wide scene that reads well behind a light left-to-right gradient.',
    sourceCollection: 'photo_gallery',
  },
};

const locationCtaDef: SlotGroupDefinition = {
  label: 'Location CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore the Neighborhood' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────

export function NeighborhoodCTAHomeSection() {
  const locationEyebrow = useContentToken('neighborhoodctahomesection.eyebrow');

  const locationImage = useSlotGroup('neighborhoodctahomesection.image', locationImageDef);
  const locationCta = useSlotGroup('neighborhoodctahomesection.cta_link', locationCtaDef);

  const galleryImages = useCollectionBinding<GalleryPhoto>(
    'neighborhoodctahomesection.gallery_images',
    {
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
  );
  const pois = useCollectionBinding<POI>('neighborhoodctahomesection.neighborhood_pois', {
    collection: 'neighborhood_pois',
    defaultLimit: 40,
  });

  const resolveTileImage = useCallback(
    (slotUrl: string | null | undefined, index: number) => {
      const fallback = galleryImages[index] ?? null;
      return slotUrl || fallback?.image || fallback?.original_url || '';
    },
    [galleryImages],
  );
  const resolveTileAlt = useCallback(
    (slotAlt: string | null | undefined, index: number, defaultAlt: string) => {
      const fallback = galleryImages[index] ?? null;
      return slotAlt || fallback?.alt_text || defaultAlt;
    },
    [galleryImages],
  );

  // The location banner is the city-themed section ("Paradise is Calling in
  // [city]"). Prefer an explicit slot image, then a real neighborhood POI photo
  // (a photo of the city), and only fall back to a gallery photo so the banner
  // never renders blank.
  const cityPhoto = useMemo(
    () => pois.find((p) => p.poi_featured_image)?.poi_featured_image ?? '',
    [pois],
  );
  const locationImageUrl =
    locationImage.image || cityPhoto || resolveTileImage(null, 3);
  const locationImageAlt = resolveTileAlt(locationImage.alt, 3, 'City view');

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div
          className="relative rounded-2xl overflow-hidden"
          style={{ minHeight: '420px' }}
        >
          {/* Background image */}
          <EditableSlotGroup
            groupPath="neighborhoodctahomesection.image"
            definition={locationImageDef}
            className="absolute inset-0"
          >
            {locationImageUrl ? (
              <VibesitesImage
                url={locationImageUrl}
                alt={locationImageAlt}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-surface)]" />
            )}
          </EditableSlotGroup>

          {/* Readability overlay: solid low-opacity white on mobile, a
              left-strong gradient fading to transparent past the midpoint on
              larger screens so the image stays viewable. */}
          <div className="absolute inset-0 pointer-events-none bg-white/65 md:bg-transparent md:bg-gradient-to-r md:from-white/95 md:via-white/55 md:to-transparent" />

          {/* Text content */}
          <div className="relative z-10 p-10 md:p-14 max-w-lg">
            <EditableToken
              path="neighborhoodctahomesection.eyebrow"
              defaultValue="Sun rays and good days"
              as="p"
              className="font-body text-sm text-black/70 mb-3 tracking-wide font-medium"
            >
              {locationEyebrow}
            </EditableToken>

            <RichTextContent
              path="neighborhoodctahomesection.heading"
              label="Location Heading"
              defaultValue='<p>Paradise is Calling in <span data-type="field-token" data-field="property_address_city"></span></p>'
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-black mb-6 leading-[1.15]"
            />

            <RichTextContent
              path="neighborhoodctahomesection.body"
              label="Location Description"
              as="div"
              className="font-body text-sm sm:text-base text-black leading-relaxed mb-8"
            />

            <EditableSlotGroup
              groupPath="neighborhoodctahomesection.cta_link"
              definition={locationCtaDef}
            >
              <a
                href={locationCta.url || '/neighborhood'}
                target={locationCta._target || '_self'}
                className="overview-intro-cta inline-flex items-center px-6 py-3 text-sm font-semibold tracking-wide text-[var(--vibe-color-text-on-secondary)] rounded-sm transition-all duration-200"
              >
                {locationCta.label || 'Explore the Neighborhood'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .overview-intro-cta {
          background-color: var(--vibe-color-secondary);
        }
        .overview-intro-cta:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodCTAHomeSection',
  displayName: 'Neighborhood CTA Home Section',
  tokens: {
    'neighborhoodctahomesection.eyebrow': {
      label: 'Location Eyebrow',
      type: 'text',
      defaultValue: 'Sun rays and good days',
    },
    'neighborhoodctahomesection.heading': {
      label: 'Location Heading',
      type: 'richtext',
      defaultValue:
        '<p>Paradise is Calling in <span data-type="field-token" data-field="property_address_city"></span></p>',
    },
    'neighborhoodctahomesection.body': {
      label: 'Location Description',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
    },
  },
  slotGroups: {
    'neighborhoodctahomesection.image': {
      label: 'Location Banner Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'photo_gallery[0].image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'City view',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Pick a property photo that conveys a sense of place — something that highlights the neighborhood or wider surroundings rather than a tight interior. Good options include an aerial or drone flyover, an exterior or rooftop view that captures the setting, or a shot featuring a recognizable nearby landmark or attraction. Favor whichever best showcases this property’s actual location.',
        context:
          'Full-bleed background image behind the home page neighborhood/location call-to-action ("Paradise is Calling in [city]"); text overlays the left side, so prefer a wide scene that reads well behind a light left-to-right gradient.',
        sourceCollection: 'photo_gallery',
      },
    },
    'neighborhoodctahomesection.cta_link': {
      label: 'Location CTA Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
        label: { label: 'Button Text', type: 'text', defaultValue: 'Explore the Neighborhood' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'neighborhoodctahomesection.gallery_images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
    'neighborhoodctahomesection.neighborhood_pois': {
      label: 'Neighborhood Points of Interest',
      collection: 'neighborhood_pois',
      defaultLimit: 40,
    },
  },
};

(NeighborhoodCTAHomeSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodCTAHomeSection;
